import { useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import { Box } from "@mui/material";
import { RecipeLayer } from "@/pages/sy/models/RecipeLayer";
import { Commodity } from "@/pages/sy/models/Commodity";
import { RecipeLayerComponent } from "./RecipeLayer";

const LAYER_DND_TYPE = "RECIPE_LAYER";

interface DragItem {
  layerId: number;
  index: number;
}

interface SortableRecipeLayerProps {
  layer: RecipeLayer;
  index: number;
  commodities: Commodity[];
  setNotificationMessage: (message: string) => void;
  initialEditMode?: boolean;
  onEditComplete?: () => void;
  totalWeight: number;
  isNewLayer?: boolean;
  onCancelNewLayer?: (layerId: number) => void;
  maxLayerNumber: number;
  disabled: boolean;
  onRecipeInUse?: (pendingLayerData: RecipeLayer) => void;
  /** Visual reorder during hover (no backend) */
  moveLayer: (dragIndex: number, hoverIndex: number) => void;
  /** Persist final position to backend */
  onDragEnd: (layerId: number, finalIndex: number) => void;
  /** Rollback if dropped outside */
  onDragCancel: () => void;
}

export const SortableRecipeLayer = ({
  layer,
  index,
  commodities,
  setNotificationMessage,
  initialEditMode,
  onEditComplete,
  totalWeight,
  isNewLayer,
  onCancelNewLayer,
  maxLayerNumber,
  disabled,
  onRecipeInUse,
  moveLayer,
  onDragEnd,
  onDragCancel,
}: SortableRecipeLayerProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const [{ isDragging }, drag] = useDrag({
    type: LAYER_DND_TYPE,
    item: (): DragItem => ({
      layerId: layer.RecipeLayerId ?? 0,
      index,
    }),
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
    end: (item, monitor) => {
      if (!monitor.didDrop()) {
        // Dropped outside - rollback
        onDragCancel();
        return;
      }
      // Persist to backend with final position
      onDragEnd(item.layerId, item.index);
    },
  });

  const [, drop] = useDrop({
    accept: LAYER_DND_TYPE,
    hover: (item: DragItem, monitor) => {
      if (!ref.current) return;

      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) return;

      // Bounding rect calculation - only swap when cursor crosses middle
      const hoverBoundingRect = ref.current.getBoundingClientRect();
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      if (!clientOffset) return;
      const hoverClientY = clientOffset.y - hoverBoundingRect.top;

      // Dragging down: only move when cursor passes middle
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
      // Dragging up: only move when cursor passes middle
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

      // Visual reorder (local state only)
      moveLayer(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
    drop: () => {
      // Return drop result so useDrag.end knows it was a valid drop
      return { index };
    },
  });

  drag(drop(ref));

  return (
    <Box
      ref={ref}
      sx={{
        opacity: isDragging ? 0.4 : 1,
        cursor: disabled ? "default" : "grab",
      }}
    >
      <RecipeLayerComponent
        layer={layer}
        commodities={commodities}
        setNotificationMessage={setNotificationMessage}
        initialEditMode={initialEditMode}
        onEditComplete={onEditComplete}
        totalWeight={totalWeight}
        isNewLayer={isNewLayer}
        onCancelNewLayer={onCancelNewLayer}
        maxLayerNumber={maxLayerNumber}
        disabled={disabled}
        onRecipeInUse={onRecipeInUse}
      />
    </Box>
  );
};
